import { FDRStatusEntry, FieldModel } from '@cibo/core'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-pro'
import { useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { usePrograms } from '../../queries'
import { ProgramIcon } from '../ProgramIcon'

const ProgramsReveal = ({ programs }: { programs: FDRStatusEntry[] }) => {
  const { t } = useTranslation('@cibo/programs/FieldProgramsColumn')
  const programContent = usePrograms()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    !!programs && setAnchorEl(event.currentTarget)
    event.stopPropagation()
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return !!programs?.length ? (
    <>
      <Button
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disableElevation
        onClick={handleClick}
        endIcon={<ArrowDropDown />}
      >
        {t('programs', { count: programs?.length ?? 0 })}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {programs?.map(({ programId }) => {
          const program = programContent.data?.find(program => program.programId === programId)
          return (
            <MenuItem key={programId} sx={{ pointerEvents: 'none' }}>
              <ListItemIcon>
                {!!program && <ProgramIcon program={program.program} size={24} />}
              </ListItemIcon>
              <ListItemText>{program?.program.content?.fields?.title}</ListItemText>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  ) : (
    // replicate button padding
    <Box px={1} py={0.75}>
      {t('programs', { count: 0 })}
    </Box>
  )
}

type ProgramsColumnProps = {
  t: TFunction
  variant: 'eligible' | 'enrolled'
} & Partial<GridColDef<FieldModel>>

export const fieldProgramsColumn = ({ t, variant, ...params }: ProgramsColumnProps) =>
  ({
    field: `${variant}Programs`,
    headerName: t(`title_${variant}`, { ns: '@cibo/programs/FieldProgramsColumn' }),
    minWidth: 200,
    flex: 1,
    sortable: false,
    filterable: false,
    resizable: false,
    renderCell: ({ row }) => (
      <ProgramsReveal
        programs={variant === 'enrolled' ? row.enrolledPrograms : row.eligiblePrograms}
      />
    ),
    ...params,
  } as GridColDef<FieldModel>)
